import React, { useMemo } from 'react';

import { Alert, Box, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { pluralizeToString } from '~/utils/stringUtils';

import {
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
  useSurchargeGridsContext,
} from '../../PricingToolSessionContext';
import SectionLabel from '../../shared/SectionLabel';
import { egsRooms, knownEgsRooms } from '../egs/egsModel';

import SurchargePeriodsSection from './SurchargePeriodsSection';
import { hasUnsetSurchargeFields } from './surchargePeriodValidation';

interface Props {
  offerId?: string;
}

export default function SurchargesTab({ offerId }: Props) {
  const { session, addPeriod, updatePeriod, removePeriod } = useSurchargeGridsContext();
  const { data: related } = useGetOpportunityRelated(offerId);
  const { roomAssets, optionState } = usePackageOptionsContext();
  const { draftIdBySfid } = usePricingToolSession();
  const { values: propertySetup, fx, currencyOptions, audRateFor } = usePropertySetupContext();
  const rooms = useMemo(() => egsRooms(optionState.roomsByCategory), [optionState.roomsByCategory]);
  const namedRooms = useMemo(
    () => knownEgsRooms(related, optionState.roomsByCategory, roomAssets, draftIdBySfid),
    [related, optionState.roomsByCategory, roomAssets, draftIdBySfid],
  );

  // fx.rate is already 1 for AUD.
  const fxRateAudToNative = fx.rate ?? null;

  const incompleteCount = session.grids.periods.filter(hasUnsetSurchargeFields).length;

  return (
    <TabPanelShell
      subtitle="Add the dated surcharge periods for this deal. Submitting sends them to Salesforce as Surcharge Periods."
      title="Surcharges"
    >
      {!fx.isLoading && fxRateAudToNative == null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set the currency and FX rate on the Property Setup tab. Until then you can still type sell amounts, but the
          converted and margin figures stay blank.
        </Alert>
      )}

      {incompleteCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pluralizeToString('surcharge period', incompleteCount)} from the import{' '}
          {incompleteCount === 1 ? 'needs' : 'need'} finishing. The workbook has no Rooms, Options, Collected by,
          Accumulate or Overlapping, so click Finish on each period below and set them before you save or submit.
        </Alert>
      )}

      <Box>
        <SectionLabel>Dated surcharge periods</SectionLabel>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          A period adds its Sell AUD on every date in its range, or only on the days you pick.
        </Typography>
        <SurchargePeriodsSection
          audRateFor={audRateFor}
          currencyOptions={currencyOptions}
          defaultCurrency={propertySetup?.currency ?? fx.currency}
          fxRateAudToNative={fxRateAudToNative}
          grids={session.grids}
          onAdd={addPeriod}
          onRemove={removePeriod}
          onUpdate={updatePeriod}
          roomNames={namedRooms}
          rooms={rooms}
        />
      </Box>
    </TabPanelShell>
  );
}
